/* The fallback dictionary — always bundled (never dynamic-imported), because
 * every other locale falls back to it for a key it hasn't got yet. Filled in
 * as each workstream in product_improvements_v2.md lands (starting with N,
 * the drill's transitions) rather than all at once — O2 is the later pass
 * that extracts everything ELSE already in the app.
 */
export default {
  /* Composable count phrases. A sentence with TWO independently pluralized
   * nouns ("3 cards · about 1 minute") cannot be one plural entry — the rule
   * picks on a single `count`. So the nouns are their own entries and the
   * sentence interpolates them, which also means a locale whose plural rules
   * differ per noun can still get both right. */
  common: {
    /* Monday-first, one letter each — the week strip on the Learn home.
     * Spanish is L M X J V S D, which is why these cannot be sliced off a
     * date format: the initials collide differently in every language. */
    dayLetters: { mon: "M", tue: "T", wed: "W", thu: "T", fri: "F", sat: "S", sun: "S" },
    /* Month abbreviations for the recap's "6 Jul – 12 Jul". The Ledger's
     * dates go through Intl instead — it never wrote its own format, so it
     * keeps delegating, just to the app's locale rather than the browser's.
     * This one DID write its own, and the day-first order is deliberate. */
    monthsShort: {
      jan: "Jan", feb: "Feb", mar: "Mar", apr: "Apr", may: "May", jun: "Jun",
      jul: "Jul", aug: "Aug", sep: "Sep", oct: "Oct", nov: "Nov", dec: "Dec",
    },
    cardCount: { one: "{count} card", other: "{count} cards" },
    minuteCount: { one: "{count} minute", other: "{count} minutes" },
    gameCount: { one: "{count} game", other: "{count} games" },
  },
  drill: {
    /* O2a · the drill's own chrome. The most-used screen in the app, so the
     * most-read strings in it. */
    phase: { learn: "WALKTHROUGH", quiz: "QUIZ", recall: "RECALL" },
    positionLeft: "{count} left",
    positionMove: "move {n} of {total}",
    exitLabel: "Exit drill",
    /* W1b · the walkthrough plays itself; these are its controls. */
    watch: {
      back: "Back two moves",
      pause: "Pause",
      play: "Play",
      skip: "Start practice →",
    },
    /* The interactive drill (DrillUI.jsx): what can be played here, by whom,
     * and what the engine thinks of it. Short on purpose — these sit in chips
     * and sheet rows on a phone, and the board is the explanation. */
    insight: {
      yours: "YOUR OPTIONS",
      theirs: "THEIR OPTIONS",
      after: "WHAT ELSE WAS THERE",
      share: "{pct}%",
      tapToPreview: "tap to see it",
    },
    prompt: {
      yourMove: "Your move",
      findMove: "Find {side}'s move",
      theyChoose: "They're choosing…",
      fromMemory: "From memory — no help",
      watching: "Watch the line",
      paused: "Paused — explore the position",
    },
    sheet: {
      book: "Book move!",
      bookStreak: "Book move! {count} in a row",
      playable: "Good move — not the line",
      inaccuracy: "Playable, but not the line",
      mistake: "That gives something away",
      blunder: "Ouch — that's a blunder",
      yours: "YOU",
      line: "LINE",
      threat: "Their best answer: {moves}",
      showThreat: "Why?",
      hideThreat: "Hide",
    },
    positionMastery: "from memory {n} of {total}",
    stage: {
      title: "Line {n} of {total}",
      first: "Start with the simplest line — walk it through once, then drill it.",
      later: "{line} — its moves, with the lines you know mixed back in.",
      review: "Review",
      yourTurn: "Your turn",
      quizMixed: "Find {line}'s moves — earlier lines are mixed in to keep you switching.",
      quizPlain: "Find the moves for {line}.",
    },
    /* R1a · the branch fan. The board draws the arrows; these only name them.
     * "eyebrow" is deliberately about the OPPONENT rather than about a
     * variation: 147 of the catalog's 162 branch points are their choice, and
     * the job at one is to recognise which reply you got. */
    branch: {
      eyebrow: "YOUR OPPONENT PICKS ONE",
      taken: "This time: {move}.",
      continue: "Continue",
      autoHint: "Tap a reply to hold it",
      held: "Held — continue when ready",
    },
    feedback: {
      tryAgain: "Try again",
      tryAgainBody: "Play {move} to continue.",
      marked: "Marked",
      markedBody: "This one comes back later in the session.",
      notQuite: "Not quite",
      notQuiteBody: "The right move was {move} — play it to continue.",
    },
    plan: "THE PLAN",
    /* N5 · the overlay that says the things a drill has to say without moving
     * the board to say them (learn/CoachOverlay.jsx). It reuses the copy that
     * is already on the screen it took over — `plan`, `phaseCard.*`, `stage.*`,
     * `streak.milestone` — so only its own two lines live here. */
    coach: {
      dismiss: "TAP TO CONTINUE",
    },
    trapSpring: "💣 THE TRAP — SPRING IT",
    trapAvoid: "💣 THE TRAP — DON'T FALL FOR IT",
    habit: "You usually play {move} here — it scores {rate}%{cost}. Find your stronger move.",
    habitCost: " and {label}",
    crowd: "📊 {games} club games (1400–2000) reach this position · {move} is played {played}% · scores {score}% for {side}",
    coachFocus: "YOUR COACH FOCUS",
    coachHabit: "In a position from this line, you often chose {move} ({rate}% in {n} games){cost}. Build the stronger habit: {better} ({betterRate}%).{even}",
    coachCost: " — the engine says it {label}",
    coachEven: " The engine's own pick here is {move} — a continuation worth learning, not just a habit to reinforce.",
    coachMore: { one: "+{count} more recurring decision tracked in this line.", other: "+{count} more recurring decisions tracked in this line." },
    srCorrect: "The correct move was {move}. Play it on the board to continue.",
    srHint: "Hint: the move to play is {move}.",
    /* N1 · the interstitial that owns a phase transition instead of letting
     * the board swap silently underneath a small text notice
     * (DrillSession.jsx#PhaseCard). Three independent fields, rendered as
     * three separate lines — not concatenated in code — so each is its own
     * whole-sentence template (O3's rule) even though English happens to
     * read them in a column. */
    phaseCard: {
      walkComplete: { one: "Walkthrough complete — {count} move", other: "Walkthrough complete — {count} moves" },
      quizStart: "Quiz — no hints",
      quizComplete: "Quiz complete",
      recallStart: "Recall — just what you missed",
      positions: { one: "{count} position", other: "{count} positions" },
    },
    /* N3a · fires only at STREAK_MILESTONES (DrillSession.jsx) — a fixed,
     * small set, not every Nth correct answer forever. */
    streak: {
      milestone: "🔥 {count} in a row",
    },
  },
  /* Move-quality grades (theme.js#QUALITY, QualityBadge.jsx) — the vocabulary
   * chess.com and lichess already taught players, so "Inaccuracy" means here
   * what it means there. "Book" is the repertoire's own move. */
  quality: {
    book: "Book",
    best: "Best",
    excellent: "Excellent",
    good: "Good",
    inaccuracy: "Inaccuracy",
    mistake: "Mistake",
    blunder: "Blunder",
  },
  /* M · notation literacy. The glyphs themselves live in learnHelpers.js and
   * are deliberately NOT translated — a glyph is the same in every language,
   * which is the entire reason M1a pre-solves the hardest piece of O (SAN
   * piece letters are language-specific: Spanish uses R/D/T/A/C). Only the
   * WORDS need a dictionary. */
  notation: {
    /* Keyed by the SAN letter, not by an invented name, so the lookup is
     * `notation.piece.${pieceLetterOf(san)}` with nothing in between. "P" is
     * reachable only from the screen-reader path, where a pawn move still has
     * to be announced as something. */
    piece: { K: "king", Q: "queen", R: "rook", B: "bishop", N: "knight", P: "pawn" },
    /* Screen-reader only — one whole sentence with named slots rather than
     * `piece + " to " + square`, per O3's rule. */
    spoken: "{piece} to {square}",
  },
  /* The Rival game's own outcome (FreePlay.jsx, gameEnd.js). Every ending is
   * a whole sentence rather than "Draw" plus a reason concatenated onto it —
   * a draw that does not say why reads as a stuck board, which is exactly how
   * this was reported by a user on 2026-08-18. */
  game: {
    side: { white: "White", black: "Black" },
    over: {
      checkmate: "Checkmate — {winner} wins",
      draw: {
        stalemate: "Draw — stalemate",
        repetition: "Draw — threefold repetition",
        material: "Draw — not enough material to mate",
        fiftyMove: "Draw — fifty moves without a capture or a pawn move",
        other: "Draw",
      },
      sub: "The game is over — take the debrief below, or exit to train again.",
    },
  },
  /* K · first-encounter explainers (FirstTime.jsx). Every one of these is
   * shown ONCE and never returns — the H3 boundary this arc is written
   * against. The permanent route back is the Help section in Settings, not a
   * standing note on any screen. */
  firstTime: {
    /* N4a · the one interruption Pablo approved (2026-08-17, "Once, yes").
     * Four sentences, before the first drill only. */
    drill: {
      title: "How a drill works",
      walkthrough: "First you'll watch the line play out, move by move — pause any time to explore.",
      quiz: "Then the board goes back to the start and you play them yourself, without help.",
      miss: "Getting one wrong is fine and costs nothing — missed moves just come back later in the session, until they stick.",
      hints: "The 💡 button turns the on-board help on and off whenever you like.",
      action: "Start the walkthrough",
      /* O5b · the same four sentences reached from Settings > Help instead of
       * as the one-time tutorial, where "Start the walkthrough" would be a
       * lie — there is no drill to start from a settings panel. */
      close: "Got it",
    },
    /* M2a · the notation key. Six rows, no prose — a legend, not a lesson. */
    notation: {
      title: "How to read the moves",
      lead: "Moves are written as the piece and the square it goes to.",
      pieces: "♚ king · ♛ queen · ♜ rook · ♝ bishop · ♞ knight",
      pawn: "A move with no piece symbol is a pawn — e4 means a pawn to e4.",
      capture: "x means a capture: ♞xe5 is the knight taking on e5.",
      castle: "O-O is castling. + is check, # is checkmate.",
      number: "The number counts move pairs, so 2.♞f3 ♞c6 is both sides' second move.",
      action: "Got it",
    },
  },
  /* U1a · the course explorer — the board you travel through instead of the
   * list you scroll. Short strings on purpose: the board is doing the
   * explaining, and every word here competes with it for a phone screen. */
  explorer: {
    exit: "← Course",
    start: "Start",
    train: "Train this →",
    keepExploring: "Keep exploring",
    /* W1g · the leaf's second, quieter way in: prove it instead of learning it. */
    testOut: "Already know it? Test out →",
    endsHere: "ends here",
    branchCount: { one: "{count} variation", other: "{count} variations" },
    leafMeta: {
      one: "{lines} line · {pct}% of games reach here",
      other: "{lines} lines · {pct}% of games reach here",
    },
    /* The toggle between the two ways of seeing a course. Both stay, because
     * a map is better for finding and a list is better for surveying — and
     * Pablo asked for the list to remain available rather than replaced. */
    viewMap: "Explore",
    viewList: "List",
    /* Names the pair of view buttons for a screen reader, which otherwise
       meets two unlabelled tabs and no way to know what they switch. */
    viewsLabel: "How to see this course",
    /* U2a · the constellation, which is a zoom level of the explorer rather
     * than a second page. The legend is one line because two variables that
     * need explaining are two variables too many. */
    zoomMap: "Whole course",
    zoomBoard: "Back to board",
    mapLabel: "Course map — every variation as a star, sized by how much lives down it and filled by how much you know",
    mapLegend: "size = how much is down there · fill = how much you know",
  },
  /* The parts of the one Settings panel this arc added (App.jsx). The rest of
   * that panel predates the i18n door and is O2a's job. */
  settings: {
    language: "Language",
    help: { section: "Help", drills: "How drills work", notation: "How to read the moves" },
    figurine: "Piece symbols in moves (♞f3, not Nf3)",
  },
  /* O2a · the session-complete screen. Every line is conditional and most are
   * one-time events (a freeze spending itself, a trap learned), so each is its
   * own whole sentence rather than a stem with clauses appended. */
  summary: {
    lineComplete: "Line complete!",
    gameOn: "🎮 Game on!",
    title: "Session complete",
    accuracy: "ACCURACY",
    xpEarned: "XP EARNED",
    /* W1e · the one-tap way into the next lesson, and today's progress. */
    continue: "Continue",
    nextUp: "Next up: {name}",
    keepGoing: "Keep going: {name}",
    todayGoal: "{done} of {target} cards today",
    todayMet: "Today's goal done — {done} cards",
    /* V2 · the summary's third tile and its review section. */
    combo: "BEST COMBO",
    missesLabel: "REVIEW YOUR MISSES",
    gameOnPitch: "Play it out against a rival at your level.",
    time: "TIME",
    masteryPerfect: "⚡ Perfect mastery run — the whole line from memory, +{xp} bonus XP",
    flawless: "⚡ Flawless — every card right first try",
    bestWas: "🏁 Personal best — {time} (was {previous})",
    bestFirst: "🏁 Personal best — {time} — your first clean run of this line",
    bestStanding: "Your best on this line is {previous}",
    bestOffIt: "Your best on this line is {previous} — {gap} off it",
    lineLearned: "Line learned — +{xp} bonus XP",
    /* W2b · a streak milestone, said once, big. W2c · a repaired streak. */
    milestone: "🔥 {count}-day streak",
    milestoneSub: "Every day for {count} days — that is how openings stick.",
    repaired: "🔧 Streak repaired — {count} days and counting",
    /* W1g · what a test-out decided. */
    testedOut: "✅ Tested out — {line} is learned, +{xp} bonus XP. From here it's review.",
    testOutMissed: {
      one: "{count} move to learn — it's in your next lesson, everything else counts as known",
      other: "{count} moves to learn — they're in your next lesson, everything else counts as known",
    },
    froze: { one: "🧊 A freeze covered yesterday — streak still at {count} day", other: "🧊 A freeze covered yesterday — streak still at {count} days" },
    earnedFreeze: "🧊 Weekly goal hit — freeze earned, {count} banked",
    trapSpring: "💣 Trap learned — you know how to spring it now",
    trapAvoid: "💣 Trap learned — you know how to dodge it now",
    /* The family session's coverage. Reported: "when I finish a drill am I
       done with the possibilities of that drill, if I do it again will I see
       different variations?" — so the answer is stated, not implied. The
       drill now opens on whatever is least learned, which is what makes the
       "next time" promise true. */
    familyLabel: "THIS GROUP OF VARIATIONS",
    familyAll: "All {total} learned. From here it's review — the same moves, spaced out to keep them.",
    familySome: "{learned} of {total} learned. Train this again and it opens on one of the {left} you haven't met yet.",
    newBadges: "NEW BADGES",
    again: "Train again",
    againMastery: "Run it again",
    learnIt: "Learn it",
    backCourses: "Back to courses",
    backLedger: "Back to Ledger",
    backCourse: "Back to course",
  },
  /* O2c · the modules that produce sentences rather than render them —
   * store.js's badges, debrief.js's verdict, briefing.js's coach voice,
   * proPitch.js, recap.js, cost.js, notifyPrefs.js.
   *
   * These were invisible to the audit until it learned to read .js, and they
   * are the app's most-read prose: the sentence after a drill, the one on the
   * home screen, the one in a notification.
   *
   * They call t() directly rather than useT(). They are plain functions, not
   * components — and their unit tests keep passing untouched, because the
   * default locale is English and t() falls back to it. */
  badges: {
    "first-steps": { name: "First Steps", desc: "Answer your first quiz position" },
    "line-learned": { name: "Line Learned", desc: "Bring a full line to learned" },
    sharpshooter: { name: "Sharpshooter", desc: "10 correct answers in a row in one session" },
    "streak-3": { name: "Warming Up", desc: "Practice 3 days in a row" },
    "streak-7": { name: "Week Warrior", desc: "Practice 7 days in a row" },
    "course-star": { name: "Rising Star", desc: "Earn your first course star" },
    "xp-500": { name: "Scholar", desc: "Reach 500 XP" },
    century: { name: "Century", desc: "100 correct answers all-time" },
    "streak-30": { name: "Month Deep", desc: "Practice 30 days in a row" },
    "xp-2500": { name: "Scholar of the Board", desc: "Reach 2,500 XP" },
    marathon: { name: "Marathon", desc: "1,000 correct answers all-time" },
  },
  /* T2b · the rank ladder the app is named after.
   *
   * The FIDE titles it used to hand out (Candidate Master at 10 lines,
   * Grandmaster at 100) are gone: those are earned, verifiable credentials,
   * and claiming one for first-pass recall of 100 opening lines is exactly
   * the kind of statement the copy test forbids. A craft ladder claims
   * nothing about playing strength, and leaves the per-course tier names
   * (Beginner / Club Player / Master) and the Rival ladder's real Elo as the
   * only places the app uses chess vocabulary that means something. */
  rank: {
    band: {
      novice: "Novice",
      apprentice: "Apprentice",
      journeyman: "Journeyman",
      adept: "Adept",
      veteran: "Veteran",
      virtuoso: "Virtuoso",
      sage: "Sage",
      oracle: "Oracle",
      luminary: "Luminary",
      legend: "Legend",
    },
    levelShort: "L{level}",
    /* Rank counts the repertoire you RETAIN, never XP — XP is what you did
       today and can be earned by re-drilling one line forever. A learned line
       is worth 1 and a mastered one 2, so the numbers here are not a line
       count and must not be phrased as one. */
    toNext: "{have} / {need} to Level {level}",
    top: "Every line in the catalog, mastered",
    gained: "Level {level}",
    bandGained: "New rank — {band}",
    known: "{known} lines known · {mastered} mastered",
  },
  tier: {
    1: "Beginner",
    2: "Club Player",
    3: "Master",
    level: "Level {tier}",
    unlocked: "unlocked",
    locked: "locked",
    pro: "Pro",
    gate: "{tier} level — {state}",
    unlockHint: "learn all {tier} lines to unlock · {done}/{total} done",
  },
  /* What a mistake cost, as a phrase rather than a number — the number is a
   * search result and pretending otherwise oversells it. */
  cost: {
    lost: "loses on the spot",
    piece: "costs a piece",
    pawnHalf: "costs a pawn and a half",
    pawn: "costs about a pawn",
    little: "costs a little",
  },
  /* The sentence after a Rival game: what happened to your prep. */
  debrief: {
    prepRanOut: "Your prep ran out on move {move}",
    prepRanOutDetail: "You played {played}; the line plays {book}.",
    gradedDetail: "You played {played}; the line plays {book}, worth about {pawns} pawns more. That is where the game turned away from your prep.",
    leftLineFree: "You left the line on move {move} — and it cost nothing",
    leftLineFreeDetail:
      "You played {played} where the line plays {book}. The engine rates them about equal, so this was a choice, not a slip.",
    blunder: "That one hurt — move {move}",
    mistake: "That was the moment — move {move}",
    inaccuracy: "Close, but — move {move}",
    theyLeft: "They went off your line on move {move}",
    theyLeftUnanswered: "{seen}. The game ended before you had to answer it.",
    theyLeftAnswered: "{seen}. You answered {reply}.",
    seenRare: "{played} — {rarity}",
    theyTried: "They tried {played} on move {move} — you handled it",
    theyTriedDetail: "{seen}, and your {reply} was as good as anything. This is the position your prep doesn't cover, and you found the move anyway.",
    theyLeftWorked: "They went off your line on move {move}, and it worked",
    theyLeftWorkedDetail: "{seen}. You answered {reply}; something else was worth about {pawns} pawns more. This is where a repertoire stops helping and you have to play chess.",
    inBook: "You stayed in book the whole way",
    inBookDetail:
      "Every move you played was your prep, and they never left it either. That is the point of drilling it.",
  },
  /* The coach's opening sentence on the Learn home. */
  briefing: {
    oneHabit: "one habit",
    thatMove: "That move",
    followUpOne: "Last time we worked on {move}. You're playing it {rate}% of the time there now — fixed.",
    followUpMany: "Last time we worked on {count} habits. {fixed} of them are fixed.",
    goingIn: "{move} is going in — you're choosing it {rate}% of the time now, up from {before}%. Not done, but moving.",
    notSeen: "That position hasn't come up in your recent games, so there's nothing to report on it yet.",
    notMoved: "We worked on {move} last time and it hasn't moved yet. Worth another go.",
    nothingBad: "{count} games since we last spoke, and nothing is going badly enough to name.",
    worstForm: "{count} games since we last spoke. You lost {losses} of {games} as {side} in the {family}.",
    habitThere: "Your habit there is {move} — {share}% of the time, scoring {rate}%. When you play {better} instead you score {betterRate}%.",
    white: "White",
    black: "Black",
  },
  /* The Pro pitch, which only ever states facts about the reader's own deck. */
  proPitch: {
    belowCap: { one: "{count} more leak sits below your top {open}.", other: "{count} more leaks sit below your top {open}." },
    belowCapDetail: "Your deck is cut to the worst of them. Pro drills the whole thing.",
    gradedDetail: "The engine graded it from your own games.",
    gradedFromMove: "The engine graded {graded} of your {open} open leaks from your own games, from move {fromMove} on.",
    graded: "The engine graded {graded} of your {open} open leaks from your own games.",
    worstLeak: "Your worst open leak {severity}.",
    openLeaks: { one: "{count} recurring leak in your games, from move {fromMove} on.", other: "{count} recurring leaks in your games, from move {fromMove} on." },
    mined: "Mined from your own history, not a generic curriculum.",
  },
  /* The weekly recap's headline and its one-line summary. */
  recapLines: {
    ratingUp: "+{delta} {speed} rating this week",
    beatOpponent: "You beat a {rating} — {gap} points above you",
    goalMet: "Goal met — {days} of {target} days trained",
    bestWin: "Your best win: a {rating}",
    trained: { one: "{count} day trained · {cards} cards", other: "{count} days trained · {cards} cards" },
    played: { one: "{count} game played", other: "{count} games played" },
  },
  /* Reminders and the weekly recap push. Delivered by the OS on native, so
   * they are read outside the app entirely. */
  notify: {
    /* W2d · the reminder that names the opening most due today. */
    openingDue: "Your {course} is due — {minutes} min",
    openingDueBody: { one: "{count} position waiting. One session keeps it.", other: "{count} positions waiting. One session keeps them." },
    freezeLeft: "🧊 {days} days, one freeze left",
    freezeBody: "Yesterday's covered — but only if today is too.",
    streakBody: "One short session keeps the streak alive.",
    weekProgress: "{done} of {target} this week",
    onTheLine: { one: "🔥 {count} day on the line", other: "🔥 {count} days on the line" },
    toGoal: { one: "{count} more session to hit your goal.", other: "{count} more sessions to hit your goal." },
    timeToTrain: "Time to train",
    fewCards: "A few cards is all it takes today.",
    cardsWaiting: "Your cards are waiting.",
    weekTitle: "Your week in chess",
    weekBody: "Best win, rating and what to work on.",
  },
  /* O2c · sign-in, account deletion, the Pro modal, reminders, and the two
   * modules that return user-facing errors from outside a component. */
  auth: {
    email: "Email",
    password: "Password",
    signingIn: "Signing in...",
    creatingAccount: "Creating account...",
    createAccountSubmit: "Create account",
    openingGoogle: "Opening Google...",
    continueGoogle: "Continue with Google",
    createAccount: "Create an account",
    back: "Back",
    recapOptIn: "Email me a weekly recap of my training. Unsubscribe in one click, any time.",
    haveAccount: "I already have an account",
    signInEmail: "Sign in with email",
    createEmailAccount: "Create email account",
    analyzeMyGames: "Analyze my games",
    errors: {
      emailInUse: "This email already has an account. Sign in instead.",
      invalidEmail: "Enter a valid email address.",
      invalidCredential: "That email or password is incorrect.",
      weakPassword: "Use a password with at least 6 characters.",
      tooManyRequests: "Too many attempts. Please try again shortly.",
      generic: "That did not work. Please try again.",
    },
  },
  account: {
    deleteTitle: "Delete your account",
    deleteWarning: "This is permanent and immediate. There is no backup to restore from, and support cannot bring it back. It deletes:",
    guestSession: "You’re in a guest session, which has no account behind it — closing the app clears it. To delete a real account, sign in to it below.",
    signInToDelete: "Sign in to the account you want to delete. We can only erase an account you can prove is yours.",
    yourPassword: "Your password",
    accountData: "Account data",
    kicker: "ACCOUNT",
    contact: "Contact",
    closing: "Closing your account…",
    clearingDevice: "Clearing this device…",

    /* What deletion destroys, item by item. A list held in a module constant,
     * which is why these are keys and not strings — a constant is evaluated
     * once at import, so a translated string in one freezes at whatever the
     * locale happened to be then. */
    goes: {
      account: "Your account and the way you sign in",
      progress: "Learning progress: XP, streak, badges, every drilled line and review card",
      ledger: "Your Ledger: imported games, opening statistics and preferences",
      feedback: "Any feedback you sent through Contact",
      device: "Everything stored on this device",
    },
    otherDevices: "Signed in on another device? Sign out there afterwards to clear its local copy.",
    /* The word the user has to type. Translated, because asking a
     * Spanish-only reader to type an English word to delete their account is
     * exactly the kind of seam this workstream exists to remove. Resolved at
     * render rather than in a module constant, so it tracks the locale. */
    confirmWord: "DELETE",
    typeToConfirm: "Type {word} to confirm",
    googleWillAsk: "Google will ask you to confirm your identity before anything is deleted.",
    confirmingYou: "Confirming it’s you…",
    wrongPassword: "That password doesn’t match this account.",
    deletingData: "Deleting your data…",
    deleting: "Deleting…",
    deleteMyAccount: "Delete my account and data",
    deleteMySession: "Delete my data and this session",
    keepMyAccount: "Keep my account",
    deleteHint: "Permanently erases your progress, games and {what}. Also available at {link}.",
    deleteHintAccount: "account",
    deleteHintSession: "session",
    askAHuman: "Prefer to ask a human? Use {link} and we’ll do it for you.",
    deleteFailed: "Your data could not be fully deleted ({error}). Nothing else was changed — please try again.",
    deletePartial: "Your data was deleted, but the account itself could not be closed ({error}). Sign in again and retry to finish.",
    /* The Learn tab while a signed-in account's progress is still on its way
     * to a device that holds none of it (just signed in, or a new phone). */
    restoringTitle: "Loading your progress…",
    restoringBody: "Bringing your training history over from your account.",
    restoringFailed: "Can’t reach your account right now. Your progress is safe there — we’ll keep trying.",
    retryNow: "Try again",
  },
  /* Small shared Learn-tab widgets (LearnUI.jsx). */
  learnUI: {
    playWhite: "You play White",
    playBlack: "You play Black",
    mostFacedTitle: "Your opponents take you here more than anywhere else in this course — {games} of your recent games",
    mostFaced: "⚑ YOU FACE THIS MOST",
    stopTitle: "Stop {n}: {done}/{total} moves learned",
    stars: "★ {stars} / {total}",
    status: { new: "NEW", started: "STARTED", learned: "LEARNED", mastered: "MASTERED" },
    firstStar: "★ first star — learn one line",
  },
  /* The coaching record — every leak the app has raised, and what happened
   * to it since. */
  record: {
    status: { active: "Working on it", improving: "Improving", fixed: "Fixed", stale: "Stopped coming up" },
    recurringDecision: "A recurring decision",
    better: " — better: ",
    raised: "raised {when}",
    fixedOn: " · fixed {when}",
    timesFixed: " · fixed {count}×",
    habitDetail: " — you chose it {share} of the time here, scoring {rate}",
    expectedDetail: " — {share} of the time now, scoring {rate}",
    betterStill: " — the engine's own pick, a continuation worth learning",
    trainLine: "train {line} →",
    closed: "You closed this one.",
    stale: "Hasn't come up in your recent games — not fixed, just not happening.",
    improving: "Going in. You're choosing the better move more often than when this was raised.",
    seenIn: "Seen in {count} recent games.",
    title: "Every leak we've raised",
    countsSome: "{fixed} closed, {open} still open.",
    countsNone: "{open} open, none closed yet.",
    emptyFixed: "Nothing closed yet. Drill an open card and come back.",
    emptyOpen: "Nothing open right now.",
    belowCap: "{count} more below your top {top}.",
    belowCapDetail: " Smaller leaks — they move up as you clear the list above.",
    drillWithPro: "♛ or drill the whole deck with Pro",
    drillOpen: "Drill the open ones →",
    filters: { open: "Open", fixed: "Fixed", all: "Everything" },
    /* One key, not two halves around a <strong>. It used to be the English
     * fragment "Nothing raised yet … on the" plus a key holding "tab and
     * they'll appear here…", which no translator could have read as a
     * sentence — see i18n.js#tAround. */
    empty: "Nothing raised yet. Cards are mined from your own games — connect an account on the {link} tab and they'll appear here as patterns emerge.",
  },
  pro: {
    kicker: "♛ TOWARDSGM PRO",
    closeAria: "Close Pro",
    createAccountFirst: "Create your account first",
    unlock: "Unlock your full repertoire",
    close: "Close",
    done: "Done",
    followsAccount: "Pro follows your account, not this device.",
    maybeLater: "Maybe later",
    planMonthly: "Monthly",
    planAnnual: "Annual",
    planAnnualHint: "Best value — billed once a year",
    startingCheckout: "Opening secure checkout…",
    checkoutError: "Couldn't start checkout. Please try again in a moment.",
    /* Shown instead of checkout while CHECKOUT_PAUSED (creemCheckout.js). */
    savingInterest: "Saving…",
    interestError: "Couldn't save that. Please try again in a moment.",
    interestTitle: "We've noted your interest",
    interestBody: "Pro isn't open for purchase yet. We've recorded that you want it, and we'll email you as soon as it's ready.",
    interestNoCharge: "You haven't been charged, and nothing on your account has changed.",
    /* The Android/iOS shells (WEB_CHECKOUT false, creemCheckout.js). These
     * must never carry a link, a button or a price: the store builds are
     * consumption-only. The website is named in plain text only, which is the
     * one thing the Payments FAQ allows there. */
    webHint: "Pro lives at towardsgm.com. Go annual for the best value, then sign in here with the same account — this app unlocks on its own.",
    handoffEmail: "Email me the link",
    handoffEmailHint: "Sent to {email} — open it on any device",
    handoffSending: "Sending…",
    handoffSentTitle: "Check your inbox",
    handoffSentBody: "We sent the link to {email}. Pick Annual, pay, and Pro unlocks here on its own.",
    handoffError: "Couldn't send the email. Copy the link instead.",
    handoffCopy: "Copy link",
    handoffCopied: "Link copied ✓",
    handoffCopyError: "Couldn't copy. Just type towardsgm.com in your browser.",
    notifyInApp: "Tell me when Pro is available in the app",
    interestBodyInApp: "Pro can't be bought in this app yet. You can get it at towardsgm.com and it unlocks here when you sign in with the same account. We've recorded that you'd like to buy it in the app, and we'll email you when you can.",
    managedElsewhere: "Pro is active on this account. Your subscription isn't managed in this app.",
    confirmingPurchase: "Confirming your purchase…",
    confirmingBody: "This only takes a moment — hang tight.",
    stillConfirming: "Still confirming",
    stillConfirmingBody: "This can take a minute after payment. If Pro doesn't unlock shortly, reopen this from the ♛ PRO button — it will pick up where it left off.",
    welcomeToPro: "You're Pro",
    welcomeBody: "Your full repertoire is unlocked, on every device you sign into.",
    manageSubscription: "Manage subscription",
    openingPortal: "Opening billing portal…",
    manageSubscriptionError: "Could not open the billing portal. Please try again.",
    complimentary: "Pro (complimentary) — no subscription to manage.",
    /* The perk list. Keys, not sentences — PERKS is a module constant, and a
     * constant is evaluated once at import. */
    perkMasterLines: "Every Master line — 470 deeper variations across all openings",
    perkNewFirst: "New openings and lines arrive in Pro first, no app update needed",
    perkWholeDeck: "Your whole mined deck, not just the twenty worst leaks",
    perkAllDevices: "One subscription, all your devices: web, Android, iOS",
  },
  reminders: {
    title: "Reminders",
    /* W2d · shown while the reminder follows the user's habit, not a picked time. */
    usualTime: "You usually train around {hour} — reminding you a little before",
    time: "Reminder time",
    recapBlurb: "Your best win, rating movement and where the week went wrong. Only when there was a week to report on.",
    emailRecap: "Email me the weekly recap",
    sentTo: "Sent to {email}. Every one carries a one-click unsubscribe — you never need this screen to stop it.",
    unsupported: "This browser can’t show notifications.",
    daily: "Daily practice reminder",
    at: "at",
    blocked: "Notifications are blocked for TowardsGM in your device settings. Turn them back on there first.",
    onlyUntrained: "Only on days you haven’t trained yet — never after you’ve done your session.",
    weeklyRecap: "Weekly recap on Monday",
    webOnly: "On the web this can only appear while TowardsGM is open in a tab. The Android app reminds you with the app closed.",
  },
  feedback: {
    signInFirst: "Please start a private session before sending feedback.",
    writeSomething: "Please write a message first.",
    sendFailed: "Could not send feedback. Please try again.",
  },
  unsubscribe: {
    incomplete: "This link is incomplete.",
    expired: "This link is no longer valid. It may already have been used, or the setting was changed since.",
    failed: "Something went wrong. Please try again, or change it in Settings.",
    working: "One moment…",
    done: "Unsubscribed",
    error: "That didn’t work",
    workingBody: "Turning off your weekly recap emails.",
    doneBody: "You won’t get the weekly recap by email again. Nothing else changed — your progress, your account and any reminders inside the app are untouched.",
    turnBackOn: "You can turn it back on any time in Settings → Reminders.",
    goToApp: "Go to TowardsGM →",
  },
  /* O2c · the last of it — board themes, the path's screen-reader labels,
   * the free-play header, the recap email, and the two words the Ledger falls
   * back to when a game has no opening name or an engine has no rating. */
  board: {
    walnut: "Classic brown",
    tournament: "Tournament green",
    slate: "Slate",
    midnight: "Midnight",
    label: "Chess board",
  },
  path: {
    mastered: "mastered",
    learned: "learned",
    started: "in progress",
    new: "not started yet",
    trapStop: "Trap stop",
    lineStop: "Line stop — {status}",
    familyStop: "Family stop, {count} related lines — {status}",
  },
  freePlay: {
    header: "GAME ON — FREE PLAY",
    exit: "Exit",
    opponent: "OPPONENT",
    opponentRating: "Opponent rating",
    fullStrength: "Full strength",

    /* The book chip above the board, and the ladder. */
    chipYourLine: "📖 your line",
    chipYourLineTitle: "Rehearsal: the opponent is staying on the line you drilled. It starts playing the crowd shortly.",
    chipInBook: "📖 in book",
    chipInBookTitle: "Still on your line — but the opponent is picking from the whole crowd now and may leave it",
    chipCrowd: "👥 crowd",
    chipCrowdTitle: "Playing what {band} players actually play here",
    chipCrowdBand: "club",
    chipOffBook: "off book",
    chipOffBookTitle: "Past the line and past the crowd's book — this is the engine now",
    ladderTopped: "You've beaten every rating this course offers.",
    /* A different sentence from ladderTopped, on a different surface: the
     * tooltip says what you did, the rung notice says what is left. */
    everyRating: "That's every rating this course offers.",
    ladderNext: "Win at {rating} to climb another rung.",
    ladderBeaten: "🪜 beaten {rating}",
    ladderNextChip: " · next {rating}",
    thinking: "Thinking…",
    yourMoveInBook: "Your move — or play something new",
    yourMove: "Your move",
    newRung: "🪜 New rung — you beat the {rating} Rival",
    newRungNext: "Next: {rating}.",
  },
  /* The one-off email behind "Email me the link" in the app (proHandoff.js). */
  proHandoff: {
    email: {
      subject: "Your TowardsGM Pro link",
      headline: "Get Pro in under a minute",
      step1: "Open the link below on your phone or computer.",
      step2: "Sign in with {email} — the same account as in the app.",
      step3: "Choose Annual: the best value, billed once a year.",
      step4: "Go back to the app. Pro unlocks on its own.",
      cta: "Get Pro on the web →",
      fallback: "Button not working? Paste this into your browser:",
      why: "You're getting this once because you asked for the link in the TowardsGM app.",
    },
  },
  emailRecap: {
    bestWin: "Best win",
    hardestWeek: "Hardest week in",
    subject: "Your chess week — {headline}",
  },
  misc: {
    /* Shown when a view names content the catalog no longer has — a line or
       family renamed by a catalog publish, or a stale deep link. Explains
       rather than apologises, and offers the door that works. */
    goneTitle: "That line has moved",
    goneBody: "The course library was updated, so this variation now sits somewhere else. Your progress is safe — open the course to find it.",
    goneBack: "Back to the course",
    unknownOpening: "Unknown opening",
    dismiss: "Dismiss",
    yourUsername: "Your chess username",
    yourStyle: "YOUR STYLE",
    train: "TRAIN",
    trainHint: "how many days a week you're aiming for",
    setup: "SETUP",
    setupHint: "rating, style and repertoire — the answers your courses are ordered by",
    mastery: "MASTERY",
    lineStage: "LINE {n}/{total} · {phase}",
    recommendedChip: "RECOMMENDED",
    includedInPro: "included in your Pro",
    coachingRecord: "COACHING RECORD",
    ledger: "Ledger",
    youPlayed: "You played",
    engineSays: " · the engine says it",
    howToRead: "How to read moves →",
    legalMovesHint: "legal moves and the suggested-move arrow, while you're learning a line",
    gameOn: "🎮 Game on!",
    hints: "hints",
    hintsDescribe: "legal moves and the suggested-move arrow, while you're learning a line",
    engine: "engine",
    engineDescribe: "the eval bar, every move's options, and your opponent's choices drawn on the board",
    sound: "sound",
    toggleOn: "on",
    toggleOff: "off",
    togglePlain: "{label}: {state}",
    toggleDescribed: "{label}: {state} — {describe}",
  },
  /* O2c · the Ledger — the OTHER tab, and until now entirely in English.
   * It is the surface a player with a game history opens first, and the one
   * whose vocabulary is most technical, so its terms are the ones a glossary
   * has to get right (form, lifetime, drift, tempo, time forfeit).
   *
   * Kept as one namespace rather than split per component: OpeningLedger.jsx
   * is one 3,000-line file by prior decision, and a dictionary shaped around
   * its internal components would encode a structure the file is explicitly
   * not supposed to grow. */
  ledger: {
    speeds: {
      ultraBullet: "Ultra",
      bullet: "Bullet",
      blitz: "Blitz",
      rapid: "Rapid",
      classical: "Classical",
      correspondence: "Corres.",
    },
    playedAsWhite: "Played as White",
    playedAsBlack: "Played as Black",
    collapse: "Collapse",
    expand: "Expand",
    typicalPosition: "Typical opening position",
    mainLine: "Main line",
    other: "Other",

    /* The opening study — the panel that opens under a row. */
    formKey: "Form — ~last {games} games",
    lifetimeAverage: "Lifetime average",
    clockBand: "Lost on the clock — the band above your form",
    mainLineInGames: "MAIN LINE IN YOUR GAMES",
    positionOccurred: "This position occurred in {n} of {total} games with moves.",
    wins: { one: "{count} win", other: "{count} wins" },
    draws: { one: "{count} draw", other: "{count} draws" },
    losses: { one: "{count} loss", other: "{count} losses" },
    recurringDecision: "RECURRING DECISION",
    afterYouLost: "After {move}, you lost {losses}/{games} games.",
    noRepeatedDecision: "No repeated losing decision clears the three-game threshold yet.",
    clockPressure: "CLOCK PRESSURE",
    timeLossDetail:
      "{timeLosses} of your {losses} losses in this opening ended on the clock ({ofLosses} of losses, {ofAll} of all {games} games).",
    noTimeForfeit: "No games in this opening ended in a time forfeit.",
    lifetime: "LIFETIME",
    current: "CURRENT",
    currentForm: "CURRENT FORM",
    trend: "TREND",
    practiceVariation: "Practice this variation in {course}",
    learnOpening: "Learn the {course} opening",

    /* Load errors. Each is a diagnosis plus the one thing to try next. */
    checkSpelling: "Check the spelling — it's the name on your {site} profile page, and it's case-insensitive.",
    playSomeGames: "Play a few standard games and open the Ledger again — everything here builds from them.",
    checkAgain: "Check again",
    siteBusy: "Nothing is wrong with your account — the site is just busy.",
    connectionMaybe: "Could be your connection, could be theirs.",
    tryAgain: "Try again",
    trySiteInstead: "Try {site} instead",
    importInstead: "Import a file instead",
    noSuchAccount: "Lichess has no account called “{username}”.",
    rateLimited: "Lichess is rate-limiting us — wait a minute and try again.",
    lichessStatus: "Lichess replied with status {status}.",
    stoppedEarly: "Stopped before any games came back.",
    noFinishedGames: "No finished standard games on {site} for “{username}” yet.",
    couldNotReach: "Couldn’t reach {site}.",
    notParseable: "Nothing parseable there — expecting a PGN or NDJSON export of one account's games (Lichess or chess.com).",
    importFailed: "Import failed — is that file a games export?",
    couldNotClear: "Couldn’t clear the stored games — check your connection and try again.",

    /* The load / update controls. */
    parsing: "Parsing · {count}",
    stop: "Stop · {count}",
    restoring: "Restoring…",
    loadGames: "Load games",
    everything: "Everything",
    hideImport: "Hide manual import",
    showImport: "Import a PGN / NDJSON export instead (username auto-detected)",
    switchAccount: "Switch account or chess site",
    readsYourGames: "This tab reads your own games",
    loadYourGames: "Load your games",
    source: "SOURCE",
    fetch: "FETCH",
    account: "ACCOUNT",
    updates: "UPDATES",
    yourGames: "Your games",
    dashboard: "Dashboard",
    analyze: "ANALYZE",
    tempo: "TEMPO",
    ratedOnly: "Rated only",
    formWindow: "FORM WINDOW",
    formWindowHint: "how many recent games the trend line averages over",
    show: "SHOW",
    showHint: "hide openings you have barely played",
    minGames: "≥ {count} games",
    variations: "VARIATIONS",
    variationsHint: "how many games a variation needs before it gets its own row",
    all: "All",
    historyComplete: "full history stored — Update only fetches games you've played since",
    fetchHint: "how much history to download, newest first{note}",
    fetchNoteMonthly: " · by monthly archive",
    fetchNoteRate: " · ~30 games/s",
    automatic: "Automatic",
    manual: "Manual",
    autoUpdateOn: "new games are fetched when you open the app and when you come back to it",
    autoUpdateOff: "the Ledger will only fetch new games when you ask it to",
    updating: "Updating…",
    updateNow: "Update now",
    openInBrowserMonth: "Open this in a browser and save what it returns (change the year/month for other archives):",
    openInBrowser: "Open this in a browser and save what it returns:",
    pastePlaceholder: "Paste PGN or NDJSON here…",
    clearing: "Clearing…",
    forgetThem: "Yes, forget them",

    /* The dashboard's own sections. */
    rating: "Rating",
    repertoireDrift: "Repertoire drift",
    openingStudy: "Opening study",
    reviewCandidate: "REVIEW CANDIDATE",
    byOpening: "By opening",
    sort: "SORT",
    sortGames: "My games",
    sortLosses: "My losses",
    sortPopular: "Popular",
    showFewer: "Show fewer",
    showAll: "Show all {total} openings ({more} more)",
    ratingBlurb: "Tracked from the rating attached to each game — pick a time control.",
    driftBlurb: "Share of games by opening family, per {unit}.",
    units: { week: "week", month: "month", quarter: "quarter" },
    trendNote: "trend = shift over ~last {games} games",
    restoringGames: "Restoring your games…",
    restoringBlurb: "Nothing is being re-downloaded from {site} — just the games you have played since last time.",
    /* The sign-in restore of an account's games from its cloud copy (LedgerRestore.jsx). */
    restoringCount: { one: "Bringing {games} game over from your account.", other: "Bringing {games} games over from your account." },
    restoreFailedTitle: "Couldn’t restore your games",
    restoreUnreachable: "Your account’s copy of your games couldn’t be reached. Check your connection and try again.",
    restoreDamaged: "The copy of your games saved in your account is damaged. Opening TowardsGM on a device that still has them repairs it — or download them again from {site}.",
    restoreRetry: "Try again",
    restoreDownload: "Download from {site}",
    nothingLoaded: "Nothing loaded yet",
    nothingLoadedBlurb: "Your openings, your win rate and your leaks all come from your games.",

    /* The page's own frame. These were the last strings on the dashboard
     * still in English, and they were the FIRST thing anyone reads — the
     * scan could not see them because Prettier had put each on its own line
     * (see scripts/i18n-audit.mjs#JSX_TEXT). */
    title: "Opening ledger",
    subtitle: "Your win rate per opening, as a trend. Draws count ½.",
    goToTraining: "Go to training →",
    /* Counts shown with thousands separators pass TWO params: `count` for the
     * plural rule and a pre-formatted `games` for display. Formatting every
     * number inside t() was the alternative and it is wrong — it would put a
     * separator in ratings and move numbers too ("1,828"). */
    gamesInView: { one: "{games} game in view", other: "{games} games in view" },
    gamesInViewOf: " of {total}",
    overallProgress: "Overall progress",
    allCombined: "All openings combined · {games} · {from} – {to}",
    formSmoothed: "Form — smoothed, ~last {games} games",
    timeForfeits: "TIME FORFEITS",
    sinceFirstGame: "SINCE FIRST GAME",
    peakLow: "PEAK / LOW",
    practiceShowsUp: "Practice shows up as gold pulling above the dashed line and staying there.",

    /* A row in the by-opening list, and the panel that opens under it. */
    rowStats: "{games} · lifetime {rate}",
    rowVsAvg: " · vs {rating} avg",
    rowOnTime: " · ⏱ {rate} on time",
    rowTimeTitle: "{losses} of {games} games in this opening ended on the clock ({rate}) — {ofLosses} of your {totalLosses} losses here",
    overallTimeTitle: "{losses} of {games} games ended on the clock ({rate}) — {ofLosses} of your {totalLosses} losses",
    openingStudyKicker: "OPENING STUDY",
    sideBoth: "Both",
    sideWhite: "White",
    sideBlack: "Black",
    asWhite: "as White",
    asBlack: "as Black",
    openingMeta: "{side} · {games} · {from} – {to}",
    noCourse: "No course covers this line yet.",
    learnByHeart: "Knowing the line by heart is the fastest way to get that time back.",
    needMovetext: "Positions need movetext. Reload from {site}, or import a PGN with moves.",
    studyIntro: "Your most repeated position after six moves, oriented as {side}",
    studySample: " — based on your most recent {games} games",
    positionOccurredExact: "This exact position occurred in {n} of {total} games with moves.",
    patternNotVerdict: " Revisit this decision in analysis; it is a pattern, not an engine verdict.",

    /* The point tooltip on the trend chart. */
    deltaPts: "{n} pts",
    filtersOn: { one: "{count} filter on", other: "{count} filters on" },
    practiceCourse: "Practice {course}",
    repertoireTip: "{label} · {games}",
    tipGame: "Game {n} · {day}",
    tipForm: "Form {rate}",
    tipLifetime: " · Lifetime {rate}",
    tipClock: " · Lost on the clock {rate}",
    tipClockOfLosses: " ({rate} of losses)",

    /* Manual import, and forgetting what was imported. */
    loadManually: "Load games manually",
    parseText: "Parse text",
    uploadFile: "Upload file",
    forgetConfirm: {
      one: "Forget the {games} stored game for {user}? Clears them on every device. Your learning progress is untouched.",
      other: "Forget the {games} stored games for {user}? Clears them on every device. Your learning progress is untouched.",
    },
    keepThem: "Keep them",
    streamingFrom: "Streaming from {site} — {progress} so far. Stop keeps what has already arrived.",

    /* Nothing to show, and why. */
    hiddenVariations: {
      one: "{count} variation with fewer than {min} games hidden ({games} games).",
      other: "{count} variations with fewer than {min} games hidden ({games} games).",
    },
    hiddenOpenings: {
      one: "{count} opening with fewer than {min} games hidden — lower the “≥ games” filter in settings to see them.",
      other: "{count} openings with fewer than {min} games hidden — lower the “≥ games” filter in settings to see them.",
    },
    noOpeningsMatch: "No openings clear the current filters. ",

    /* Relative time, and the status suffixes that trail the games-in-view
     * line. Each is a fragment by design — they are appended in a chain and
     * the order they appear in is data, not language. */
    justNow: "just now",
    minutesAgo: "{count}m ago",
    hoursAgo: "{count}h ago",
    daysAgo: "{count}d ago",
    fullHistory: " · full history ✓",
    syncedAgo: " · synced {ago}",
    importedFile: " · imported file",
    restoredFromAccount: " · restored from your account",
    newGames: { one: "+{games} new game", other: "+{games} new games" },
    newGamesAuto: { one: "+{games} new game · auto-synced", other: "+{games} new games · auto-synced" },
    upToDate: "already up to date",
    usernamePlaceholder: "{site} username",
    rangeLast: "last {count} of {total} downloaded",
    rangeAll: "all {total} downloaded games",
    rangeInstant: "applies instantly — no re-download",
    openSettings: "Open settings",
    widenFilters: " to widen the tempo filter or lower the minimum games.",
  },
  /* O2a · the Learn tab's home — the screen between drills. */
  home: {
    title: "Opening academy",
    promise: "Drills built around the openings you actually play.",
    /* X3 · the row of courses already started, above the full grid. */
    yourCourses: "Your courses",
    side: "SIDE",
    sideBoth: "Both",
    sort: "SORT",
    sortPopular: "Popular",
    sortGames: "My games",
    sortLosses: "My losses",
    yourTrainer: "Your trainer",
    redoSetup: "Redo setup",
    connectAccount: "Connect an account →",
    connectNote: "Playing online? Connect Lichess or Chess.com and this stops guessing — the coach works from your own games.",
    leakFixed: { one: "🎉 You closed a leak", other: "🎉 You closed {count} leaks" },
    leakPlaying: "You're playing {move} there now. ",
    dismiss: "Dismiss",
    fromGames: "From your games",
    startTraining: "Start training",
    continueTraining: "Continue training",
    reviewDue: "Review due positions",
    coachStop: "Coach focus stop — recurring mistakes from your own games",
    coachHeading: "Coach focus: your mistakes in this opening",
    masteryTitle: "Mastery run: the whole line from memory — no hints, on the clock",
    testOut: "Test out",
    testOutTitle: "Already know this line? Play it from memory — every move right and it's marked learned",
    yourMistakes: "Your Mistakes",
    todaysSession: "Today's session",
    ledger: "Ledger",
    allCourses: "All courses",

    /* The course grid, the path, and the line rows inside it. */
    measuredFromGames: "Measured from your own recent games.",
    seeYourRecord: "See your record →",
    linesLearned: "{learned}/{total} lines learned",
    dueShort: " · {count} due",
    lockedMore: " · 🔒 {count} more",
    proShort: " · ♛ {count} pro",
    cardGames: { one: "{count} game", other: "{count} games" },
    cardLosses: { one: "{count} loss", other: "{count} losses" },
    cardNoGames: "No games yet",
    dueForReview: " · {count} due for review",
    proLines: " · ♛ {count} pro lines",
    whiteRepertoire: "♙ White repertoire",
    blackRepertoire: "♟ Black repertoire",
    eloRange: "Elo {range}",
    relatedLines: "{count} related lines, one at a time",
    stopIndex: " · stop {n}",
    orderedForYou: "⚑ Ordered for you — the variations your own opponents actually play come first",
    orderedForYouCount: " ({count} of your recent games reach this opening)",
    coverage: "You've met this opening {faced}× as {side} — {matched} of those reached a variation trained below. The rest branched off before any taught line or into one the course doesn't cover yet.",
    pathResumes: "↑ Your path resumes here — this button always picks up your next stop, wherever you left off.",
    gotIt: "GOT IT",
    unlockHint: "learn all {tier} lines to unlock · {done}/{total} done",
    earnedUnlock: "you've earned this level — unlock with Pro",
    deepestUnlock: "the deepest theory — unlock with Pro",
    branchLines: "{count} lines · one at a time",
    /* Q1c · the same chip when some of the family's lines repeat moves the
     * others already teach, so the session is shorter than the line count
     * suggests. Terse on the chip (it is 9.5px and must not wrap), with the
     * whole explanation in its title. */
    branchShared: "{total} lines · {drill} to drill",
    branchSharedTitle: "{shared} of these {total} lines repeat moves the others already teach — drilling {drill} covers all {total}.",
    pastYourPrep: "Past your prep: {line}",
    recurringSlips: {
      one: "⚠ {count} recurring slip past your prep",
      other: "⚠ {count} recurring slips past your prep",
    },
    trapAvoid: "AVOID IT",
    trapSpring: "SPRING IT",
    asWhite: "White",
    asBlack: "Black",
    yourGamesTitle: "Your games reaching this line as {side}: {wins} won, {losses} lost",
    youGames: "you: {games} · ",
    winPct: "{pct} W",
    lossCount: " · {count} L",
    mistakesIn: "Your mistakes in {course}",
    positionsFromGames: "{count} positions from your games",
    cardsAcross: {
      one: "{count} card across your courses",
      other: "{count} cards across your courses",
    },
    linesInOrder: "{count} lines, learned in order",
    dueChip: "{count} due",
    coachFixed: "{done}/{total} fixed",
    branchLearned: "{done}/{total} learned",
    familyAndMore: "{first} & {count} more",
    /* T1a · the folded tail of a tier's locked Pro previews. Three separate
     * strings rather than one sentence with a button glued on: the label is
     * read by the path node, the body by the row, and the affordance by the
     * control, and each has to stand alone (O3's rule). */
    lockedRest: {
      one: "{count} more Master variation, folded away",
      other: "{count} more Master variations, folded away",
    },
    lockedRestShow: "show all →",
    /* T2a · the share of the course's games that travel down a mapped fork.
     * A bar carries the comparison; this carries the value for anyone reading
     * the page rather than looking at it. */
    mapShare: "{pct}%",
    lockedRestLabel: {
      one: "{count} more locked variation",
      other: "{count} more locked variations",
    },
  },
  /* O2a · the app shell — tab bar, landing, auth, and the Settings panel's
   * own labels.
   *
   * NOT the privacy policy or the other info pages. Those are a legal
   * statement about what the app does with data, and the Play data-safety
   * declaration is derived from them 1:1 (store/play/LISTING.md §3). A
   * machine-translated policy that drifts from the English one is a
   * compliance problem rather than a UX one, so they stay in English until a
   * human writes the Spanish. See "Questions for you" in
   * product_improvements_v2.md. */
  app: {
    tabLedger: "LEDGER",
    tabLearn: "LEARN",
    tabPlay: "PLAY",
    tabProgress: "PROGRESS",
    account: "Account and settings",
    home: "Go to the TowardsGM landing page",
    settings: "Settings",
    done: "Done",
    signIn: "Sign in",
    signOut: "Sign out",
    closeSignIn: "Close sign in",
    keepProgress: "Keep your progress",
    guest: "Playing as a guest",
    guestNote: "Progress stays on this device only",
    signedNote: "Progress syncs to every device",
    board: "Board",
    drills: "Drills",
    privacy: "Privacy",
    terms: "Terms",
    licenses: "Open source licenses",
    contact: "Contact",
    sound: "Sound effects",
    legalMoves: "Show legal-move hints",
    analyticsOptIn: "Help improve TowardsGM with anonymous usage analytics",
    settingsFollowAccount: "Settings follow your account on every device once you sign in.",
    analyticsTitle: "Help improve your chess trainer",
    analyticsBody: "Allow anonymous usage analytics to help us understand which practice paths work. We never send game moves, usernames, emails, or feedback text to Analytics.",
    analyticsAllow: "Allow analytics",
    analyticsNo: "No thanks",
    /* The landing's first screen and feature list (adLanding.js). `title` is
     * an ad's promise, short enough for two lines on a phone; `body` is the
     * line under it in the feature list, so the two pictures that are only
     * ever a first screen (the drill, the composite) have none. */
    landingAds: {
      study_sicilian: {
        title: "Your score in every opening you actually play",
        body: "Import your Lichess or Chess.com games and see where you win, where you lose, and the decision that keeps costing you.",
      },
      today_habit: {
        title: "Find the moves you keep playing that cost you",
        body: "Your coach spots the habits in your own games that lose points, and the move that scores better.",
      },
      last_games: {
        title: "The moves that cost you in your last games",
        body: "An engine checks your latest games, finds the moments that cost you, and lets you play just those again.",
      },
      drill_habit: {
        title: "Drill your openings, starting from your own habits",
      },
      prep_courses: {
        title: "The opening course that fits how you play",
        body: "Your prep report flags the decisions you keep facing in your games and recommends the course to learn next.",
      },
      course_pirc: {
        title: "A course for every opening you play",
        body: "23 opening courses. Walk the lines on the board, see how often your games reach each one, and train it move by move.",
      },
      coach_ledger: {
        title: "Your personal opening coach",
      },
    },
    landingFromGames: "Built from your own Lichess and Chess.com games. Free, no account needed.",
    landingMoreKicker: "How it works",
    /* The composite ad's three steps, one over each feature. */
    landingSteps: {
      read: "Reads your games",
      find: "Finds your leaks",
      train: "Trains the fix",
    },
    landingEndTitle: "Start with your own games",
    landingTagline: "YOUR CHESS TRAINER",
    getOnGooglePlay: "Get it on Google Play",
    pricingKicker: "PRICING",
    pricingBody: "Free forever for the Ledger and most of the Learn curriculum. Pro — €5/month or €36/year, cancel anytime — unlocks the deep tail of the catalog and your full mistakes deck.",
    continueTraining: "Continue training →",
    welcomeBack: "Welcome back",
    welcomeBackNote: "Your progress is saved. Jump back into your training path.",
    continueToTrainer: "Continue to your trainer →",
    /* O2c · the shell's last English. The privacy policy stays in English by
     * Pablo's instruction (2026-08-18) — a legal text is a translation
     * liability, and the Play data-safety declaration derives from it 1:1 —
     * so only the chrome AROUND it is extracted through this dictionary: the
     * back button, the footer link, and the Contact form the same page
     * hosts. Terms of Service (added 2026-08-20) IS translated, but as a
     * hand-written pair (App.jsx's TERMS_EN/TERMS_ES) rather than through
     * this dictionary, for the same reason the privacy policy's own prose
     * never moved here: it reads as one document per language, not as
     * lookup keys assembled at render time. */
    close: "Close",
    backToApp: "← Back to TowardsGM",
    accountAndSettings: "Account and settings",
    homeTitle: "TowardsGM home",
    proTitle: "TowardsGM Pro",
    broke: "Something broke",
    contactKicker: "CONTACT",
    contactTitle: "Help shape your chess trainer",
    contactBlurb:
      "Tell us what helped, where a line felt wrong, or what would make your training more useful. Messages go to the product inbox, not Google Analytics.",
    contactTopic: "Topic",
    contactEmail: "Email (optional)",
    contactMessage: "Message",
    contactSend: "Send feedback",
    contactSending: "Sending...",
    contactThanks: "Thanks - your note is in the coach inbox.",
    topics: {
      product: "Product feedback",
      bug: "Bug report",
      content: "Opening content",
      privacy: "Privacy request",
    },
    beginNote: "Start right away, privately. An account keeps your progress across devices.",
    deleted: "Your account is deleted",
  },
  /* O2a · the three report cards on the Learn home — Today, the weekly recap,
   * and the prep report. Reported by Pablo as untranslated. */
  /* W3d · the home card after an import: the trained lines, holding up in real games. */
  prepHeld: {
    title: {
      one: "🛡️ Your prep held — {course}, {moves} moves deep in {count} game",
      other: "🛡️ Your prep held — {course}, {moves} moves deep in {count} games",
    },
    xp: "+{xp} XP for playing what you trained",
    more: { one: " · and {count} more opening", other: " · and {count} more openings" },
    dismiss: "Dismiss",
  },
  /* W3e · a Rival boss at the end of each level. */
  boss: {
    offer: "⚔️ Boss: beat the {rating} Rival to close {tier}",
    rematch: "⚔️ {tier} boss beaten — rematch at {rating}",
    fullStrength: "Full strength",
    won: "⚔️ Unit complete — you beat the {rating} Rival",
    newRecord: "⚔️ New boss record — {rating}",
  },
  /* X1 · the Play tab (PlayPage.jsx): one card per game, each saying what it is
   * before a tap. Kickers are uppercase on purpose, like every kicker here. */
  play: {
    title: "Play",
    subtitle: "Games built from the openings you know",
    dailyKicker: "DAILY OPENING · #{number}",
    dailyTitle: "Find today's book move",
    dailySolved: "Solved today",
    dailyMissed: "Missed today",
    dailyGo: "Play today →",
    dailyResult: "See today's result →",
    rivalKicker: "RIVAL",
    rivalLine: "A real game against the Rival, from this opening",
    rivalBest: "Best win: {rating} · next {next}",
    rivalTopped: "Ladder climbed at {rating} — try full strength",
    rivalGo: "Play →",
    bossKicker: "BOSS · {tier}",
    bossLine: "Beat the {rating} Rival to close {tier}",
    bossGo: "Fight →",
    rushKicker: "OPENING RUSH",
    rushTitle: "3 minutes · 3 strikes",
    rushBest: "Best {best}",
    rushNoBest: "No record yet",
    rushPool: { one: "{count} position you know", other: "{count} positions you know" },
    rushGo: "Play →",
    rushLocked: "Learn {count} more positions to unlock",
  },
  /* W3b · the Daily Opening: one shared position a day. */
  daily: {
    entrySub: "One position, the same for everyone today",
    kicker: "DAILY OPENING #{number}",
    findWhite: "White to play — find the book move",
    findBlack: "Black to play — find the book move",
    triesLeft: { one: "{count} try left", other: "{count} tries left" },
    notIt: "Not the book move — try again",
    solved: "Solved — {course}",
    missed: "The move was {move} — {course}",
    streak: { one: "🔥 {count} day", other: "🔥 {count} days" },
    share: "Share",
    /* The first line of the shared text — never a move, so it cannot spoil anyone's day. */
    shareTitle: "TowardsGM Daily Opening #{number}",
    /* The celebration on the shared picture. What the player DID, said the way
       a person would say it — "Solved in 1 try" is a database row, "First try!"
       is the thing worth posting. Never a move, like everything else here. */
    shareFirstTry: "First try!",
    shareSolvedIn: { one: "Solved in {count} try", other: "Solved in {count} tries" },
    shareMissedIt: "Missed today's move",
    /* The line that has to make a stranger tap: a question they can answer by
       looking at the position above it, never a boast about the sender. */
    shareInvite: "Can you find the book move?",
    shareCta: "Play today's puzzle — free",
    copied: "Copied — paste it anywhere",
    comeBack: "A new position tomorrow",
    exit: "Leave the Daily Opening",
  },
  /* W3c · swipe review: the due cards, one thumb at a time. */
  swipe: {
    entry: "📱 Swipe review",
    entrySub: { one: "{count} position due · one thumb", other: "{count} positions due · one thumb" },
    button: "📱 Swipe",
    findWhite: "White to play",
    findBlack: "Black to play",
    progress: "{n} of {total}",
    hint: "Find the move — swipe up or skip to pass",
    theMove: "The move was {move}",
    next: "Next ↑",
    skip: "Skip",
    exit: "Leave the review",
    doneKicker: "REVIEW DONE",
    doneTitle: { one: "{count} position reviewed", other: "{count} positions reviewed" },
    doneStats: "{right} right · {skipped} skipped",
    nothing: "Nothing reviewed this time",
    back: "Back",
  },
  /* W3a · Opening Rush: three minutes, your repertoire, three strikes. */
  rush: {
    findWhite: "White to play — find the move",
    findBlack: "Black to play — find the move",
    hint: "Your repertoire, as fast as you can",
    missed: "The move was {move}",
    strikesLabel: "{count} of {total} strikes",
    exit: "Leave the Rush",
    over: "RUSH OVER",
    firstRecord: "Your first record — now beat it",
    newRecord: "New record — your best was {previous}",
    record: "Your record is {best}",
    again: "Run it again",
    back: "Back",
  },
  /* W2a · today's three quests and the chest for closing the set. */
  quests: {
    kicker: "TODAY'S QUESTS",
    bookMoves: "{target} book moves",
    perfectLesson: "A lesson with no misses",
    combo: "{target} right in a row",
    twoSessions: "Finish {target} sessions",
    openChest: "🎁 Open chest",
    rewardXp: "🎁 +{xp} XP",
    rewardFreeze: "🎁 A streak freeze, banked",
    done: "✅ Quest done: {quest}",
    allDone: "🎁 All three quests done — your chest is waiting in Progress",
  },

  today: {
    kicker: "TODAY",
    doneForToday: "Done for today",
    nothingDue: "Nothing due right now",
    plan: "{cards} · about {minutes}",
    comeBack: "Come back tomorrow, or get ahead with a new line below.",
    startBelow: "Start a line below and it'll come back to you on a schedule.",
    overflow: "{count} more due — do the lot →",
    startSession: { one: "{count} card · {minutes} min →", other: "{count} cards · {minutes} min →" },
    browseCourses: "Browse courses",
    startCourse: "Start {course} →",
    /* W2e · the home screen's one-tap way back into the drill. */
    continueStop: "Continue: {name} →",
    orContinue: "or continue {name}",
    badgeProgress: "{done} of {total}",

    /* The week line under the Progress tab's streak card (X2 moved it off TODAY). */
    weekMet: "✓ {done} of {target} this week — goal met",
    weekLeft: {
      one: "{done} of {target} this week · {daysLeft} day left",
      other: "{done} of {target} this week · {daysLeft} days left",
    },
    /* W2c · streak repair: the offer, then what accepting it now depends on. */
    repairOffer: "🔧 Repair your {count}-day streak",
    repairArmed: "🔧 Finish a session today and your {count}-day streak carries on",
    prepReport: "YOUR PREP REPORT",
    leaksClosed: { one: "✓ {count} leak closed · ", other: "✓ {count} leaks closed · " },
    coachingRecordLink: "Coaching record ({count}) →",
    freezesBanked: {
      one: "{count} streak freeze banked — covers one missed day automatically",
      other: "{count} streak freezes banked — covers one missed day automatically",
    },
  },
  /* X2 · the Progress tab (ProgressPage.jsx): the streak, today's quests, the
   * rank and the badges, one card each. The week line, the freezes, the repair
   * and a badge's progress keep their `today.*` keys. */
  progress: {
    title: "Progress",
    subtitle: "Your streak, today's quests and the climb to GM",
    streakUnit: { one: "day streak", other: "day streak" },
    streakStart: "Train today to start a streak",
    streakSafe: "Safe for today — see you tomorrow",
    streakAtRisk: "Train today to keep it",
    streakFreezeCovers: "A freeze covers yesterday — train today to keep it",
    streakLapsed: {
      one: "Your {count}-day streak ended — train today to start again",
      other: "Your {count}-day streak ended — train today to start again",
    },
    chestLocked: "Finish all three to open today's chest",
    rankKicker: "RANK",
    xpTotal: "XP earned",
    badgesKicker: "BADGES",
  },
  /* X2 · the status strip on Learn and Play (StatusStrip.jsx). Read aloud in
   * place of the pills, which are an emoji and a number. */
  status: {
    streakLabel: { one: "{count}-day streak — open Progress", other: "{count}-day streak — open Progress" },
    questsLabel: "Today's quests: {done} of {total} done — open Progress",
    questsChest: "Today's quests are done — your chest is ready",
    xpLabel: "{xp} XP — rank and badges",
  },
  recap: {
    kicker: "YOUR WEEK · {label}",
    ratingKicker: "{speed} RATING",
    dismiss: "Dismiss weekly recap",
    trained: "TRAINED",
    trainedDays: "{days} of {target} days",
    trainedCards: " · {cards}",
    playedGames: "{games}",
    playedWdl: " · {wins}W {draws}D {losses}L",
    played: "PLAYED",
    bestWin: "BEST WIN",
    hardest: "Hardest week in",
    hardestDetail: { one: "{family} — {rate}% over {games} game", other: "{family} — {rate}% over {games} games" },
    drillCourse: "Drill {course} →",
  },
  /* "Your last games" — the engine's read on the games you actually played
     (learn/recentReview.js). Deliberately about ONE game each: the opponent
     and the result locate the moment in the memory of playing it, which a
     date cannot do. */
  review: {
    title: "Your last games",
    checking: "Checking for new games…",
    reviewing: "Reviewing game {done} of {total}…",
    vsNameRating: "vs {name} ({rating})",
    vsName: "vs {name}",
    vsRating: "vs a {rating}",
    vsUnknown: "a recent game",
    won: "you won",
    lost: "you lost",
    drew: "you drew",
    moment: "move {move} · you played {played} · {cost}",
    train: { one: "Play this moment again", other: "Play these {count} moments again" },
    clean: { one: "Nothing to flag in your last game — clean opening play.", other: "Nothing to flag in your last {count} games — clean opening play." },
    sessionName: "From your last games",
  },
  prep: {
    flagged: { one: "{count} recurring decision flagged across your games", other: "{count} recurring decisions flagged across your games" },
    drillThem: "Drill them →",
    trend: "Since starting {course}: {speed} rating",
    recommended: "Recommended next: {course} — fits how you've been playing lately",
    unmatched: "{count} of them fall outside the {courses} courses — drill just those →",
    depth: { one: "♛ {count} more leak ranked below your top {top} — Pro drills the whole deck", other: "♛ {count} more leaks ranked below your top {top} — Pro drills the whole deck" },
  },
  /* J · the onboarding wizard (Onboarding.jsx, onboarding.js). Only the
   * strings this workstream ADDED — J1c's beginner style question, J5a's
   * beginner rating choices, J2a's escape hatch, J3a's caption. The rest of
   * the wizard's copy (STYLE_QUESTIONS' blurbs, every other screen) predates
   * the i18n door and is O2's job to extract, one surface per PR, rather than
   * half-migrating this file opportunistically. */
  wizard: {
    /* O2a · the whole wizard, extracted. `onboarding.js` now carries ids,
     * moves, tags and course routing — the CHESS — and none of the prose, so
     * there is one source of truth per string rather than a data file and a
     * dictionary drifting apart. Its unit tests still pass untouched, which is
     * the check that the split kept the logic and moved only the words. */
    step: {
      welcome: { kicker: "WELCOME", title: "Let's set up your trainer", sub: "A few quick questions, about a minute, and you'll finish inside your first lesson. Skip any of them." },
      welcomeRedo: { kicker: "SETUP", title: "Update your trainer", sub: "Your answers are already filled in — change what's changed, and skip the rest." },
      online: { kicker: "STEP 1", title: "Do you play chess online?", sub: "If you do, we can learn from games you've already played instead of guessing." },
      connect: { kicker: "STEP 2", title: "What should we call you there?", sub: "Your {host} username — the one on your profile page." },
      offline: { kicker: "STEP 2", title: "No problem", sub: "Nothing here needs a game history — the answers below are enough to build you a plan." },
      experience: { kicker: "STEP 3", title: "How much opening theory do you know?", sub: "A 1500 who's never opened a book and a 1500 who knows the Najdorf by heart need different first lessons." },
      rating: {
        kicker: "STEP 4",
        title: "How strong are you?",
        subOnline: "Roughly — we'll correct it from your games as soon as they're in.",
        subOffline: "Roughly. This sets how deep your lessons go, not what you're allowed to see.",
      },
      style: { kicker: "YOUR STYLE · {n} OF {total}", title: "Pick the move you'd actually play" },
      repertoire: { kicker: "STEP 5", title: "As White, you open with…", sub: "Just your first move. We'll start you in a course built around it." },
      goal: { kicker: "STEP 6", title: "How often do you want to train?", sub: "Pick something you'll actually hit. Changeable whenever." },
      done: { kicker: "READY", title: "Your first lesson", sub: "Here's where we're starting you. Three moves — under a minute." },
      doneRedo: { kicker: "READY", title: "That's the update", sub: "Your courses re-order around this the moment you save." },
    },
    nav: { back: "← Back", skip: "Skip setup", cancel: "Cancel", cont: "Continue", save: "Save", start: "Start my first line →" },
    online: { lichess: "Lichess", chesscom: "Chess.com", both: "Both", no: "Not yet", noHint: "That's completely fine — everything below still works." },
    experience: {
      new: { title: "Just starting out", hint: "I know how the pieces move" },
      casual: { title: "I play, I don't study", hint: "Openings are mostly improvised" },
      named: { title: "I know my openings by name", hint: "A few lines, a few moves deep" },
      studied: { title: "I study seriously", hint: "I have a real repertoire" },
    },
    style: {
      risk: {
        prompt: "You're White. Both sides are developed. What now?",
        gambit: { title: "Offer the pawn", blurb: "b4 — the Evans Gambit. Give up material to rip the position open while they're still developing." },
        build: { title: "Build slowly", blurb: "d3 — the Giuoco Pianissimo. Nothing forcing. Improve every piece and squeeze later." },
      },
      theory: {
        prompt: "You're White again, one move in. Which of these sounds like you?",
        system: { title: "The same setup every game", blurb: "Bf4 — the London. One structure you know cold, against almost anything they try." },
        mainline: { title: "The main lines, theory and all", blurb: "c4 — the Queen's Gambit. More to learn, and more to play for once you've learned it." },
      },
      asblack: {
        prompt: "Now you're Black and they've played 1.e4. Your answer?",
        counter: { title: "Unbalance it immediately", blurb: "1...c5, the Sicilian. Refuse to copy them. Sharpest way to play for a win with Black." },
        solid: { title: "Build something they can't break", blurb: "1...c6, the Caro-Kann. No weaknesses, no drama, and a pleasant endgame if they overpress." },
      },
    },
    opener: {
      e4: { title: "1.e4", hint: "Open games, quick development" },
      d4: { title: "1.d4", hint: "Space, structure, slower burn" },
      other: { title: "Something else", hint: "English, Bird, Réti…" },
      unsure: { title: "Not sure yet", hint: "Pick one for me" },
    },
    /* Numeric bands read the same in every language; only the two that are
     * words need a translation, and they live here with the rest so the
     * component has one lookup rather than a conditional. */
    band: { u800: "Under 800", "800-1200": "800–1200", "1200-1600": "1200–1600", "1600-2000": "1600–2000", "2000+": "2000+", unsure: "Not sure", unsureHint: "We'll assume you're somewhere in the middle and adjust as we learn." },
    goal: {
      casual: { title: "Casual", hint: "2 days a week" },
      regular: { title: "Regular", hint: "4 days a week" },
      serious: { title: "Serious", hint: "6 days a week" },
    },
    transparency: {
      heading: "WHAT THAT MEANS",
      downloadLabel: "We download", download: "your public game archive, through the site's own API. No password — we never ask for one.",
      storedLabel: "It's stored", stored: "on this device and in your account. Not shared, not sold, never used to train anything.",
      computeLabel: "We compute", compute: "which variations you face, the moves that lose you games, and your rating trend.",
      eraseLabel: "You can erase it", erase: "any time, from Settings → Delete account.",
    },
    offlineNote: "Start playing online later and you can connect an account from the {tab} — everything you've done {carries}.",
    offlineTab: "Ledger tab",
    offlineCarries: "carries over",
    doneSide: { white: "AS WHITE", black: "AS BLACK" },
    doneTuned: " · TUNED FOR {band}",
    doneSaved: "Saved {name}. Your games download the first time you open the Ledger, and this plan rebuilds around them.",
    doneImport: "Import my games first →",
    connectSkip: "I'll add it later",
    skipCaption: "available later in Settings",
    /* J1c — ids match BEGINNER_STYLE_QUESTION.options[].id (onboarding.js). */
    beginnerStyle: {
      heading: "When you play, what usually goes wrong?",
      prompt: "No position for this one — just what usually happens.",
      options: {
        attacked: { title: "I get attacked early", hint: "My king ends up in trouble before I'm ready for it." },
        stuck: { title: "I run out of ideas", hint: "I don't know what to do once the opening moves run out." },
        passive: { title: "I never get to attack", hint: "The other side always seems to be the one doing something." },
      },
    },
    /* J5a — ids match BEGINNER_RATING_CHOICES[].id (onboarding.js). */
    beginnerRating: {
      u800: "Never played a rated game",
      "800-1200": "I've played a bit online",
      pick: "I know roughly where I stand",
    },
  },
};
